// Paying for it and hearing about it: billing, LLM routing and system prompts,
// the integrations and connected webhook sources work arrives through, donations,
// the notification/push/email fan-out, and outbound webhooks.
// Split out of `../capability-manifest.ts`, which is now the barrel over this
// directory. A GROUP, never a meaning: a capability belongs to whichever group
// its `REST_ROUTES` neighbours are in, so these are absent from the barrel and a
// caller reads `CAPABILITY_MANIFEST`. `./manifest` is the only place they compose,
// and `capability-manifest.test.ts` proves the five are a PARTITION of it.

import { PUSH_IS_BROWSER } from './reasons';
import { type Capability, gap, mcp, na, yes } from './surface';

export const COMMERCE_CAPABILITIES: Readonly<Record<string, Capability>> = {
  // ── billing ───────────────────────────────────────────────────────────────
  'billing.keys_list': { rest: yes, mcp: mcp('billing.keys_list'), ui: yes },
  'billing.key_add': { rest: yes, mcp: mcp('billing.key_add'), ui: yes },
  'billing.key_verify': { rest: yes, mcp: mcp('billing.key_verify'), ui: yes },
  'billing.key_delete': { rest: yes, mcp: mcp('billing.key_delete'), ui: yes },
  'billing.key_rotate': { rest: yes, mcp: mcp('billing.key_rotate'), ui: yes },
  // arc 101 slice 02 — provider model enumeration, on all three surfaces: the
  // dashboard's picker (step 4) and the MCP capture shape (step 6). The slice
  // exists precisely because a maintainer must be able to pick a model from chat
  // as well as from the wizard — a verb that could only mint UNTAGGED keys while
  // the wizard minted tagged ones is two capture shapes for one object.
  'billing.provider_models': {
    rest: yes,
    mcp: mcp('billing.provider_models'),
    // Slice 02 step 4 landed: `<AddKeyModal/>` probes this route in its
    // credential phase and the picker is what phase two offers.
    ui: yes,
  },
  'billing.key_models': {
    rest: yes,
    // NOT step 6's, and deliberately still open. Step 6 closed the PRE-capture
    // shape — enumerate, pick, tag, capture — which is what the capture doors
    // need. Re-listing a STORED key is the refresh a human triggers by OPENING
    // the key (step 7), and no verb re-tags a stored key's catalogue yet, so an
    // MCP twin today would answer a question its caller could not act on.
    //
    // RE-EXAMINED, plan 101 slice 04, against the one caller said to need it:
    // `TASK_STALL_REMEDY[NoUsableKeys]` names a key "valid with the model it names
    // parked in its own catalogue". That per-key catalogue is ALREADY readable
    // here — `billing keys_list` serves the same `maskKey` the REST list does, and
    // its `models[]` carries `active: false` on exactly the parked row, so the
    // question "which model is parked" is answered without this route. What the
    // route adds is a LIVE re-read of the provider's list for a stored key, and the
    // act it would feed (re-tagging) is `billing key_model_set` since #7810, and the
    // catalogue sync (#7810) re-lists every valid key on its own, so no caller needs it.
    mcp: gap('re-listing a stored key’s models is arc 101 slice 02 step 7, on both surfaces'),
    ui: gap('the key-detail model refresh is arc 101 slice 02 step 7'),
  },
  // #1247: documented edit path for the org pool's `kind` — `PATCH
  // /v1/billing/keys/:id/kind`, and since capacity-aware scheduling the `billing
  // key_kind_set` action (a subscription key is held to its capacity), both on the
  // org's `write:billing` scope, never the `account` resource's self-serve one.
  'billing.key_kind_set': { rest: yes, mcp: mcp('billing.key_kind_set'), ui: yes },
  // plan 102 slice 05b: order the org pool (`byok_keys.priority`), audited
  // `billing.key.priority_changed`.
  'billing.key_priority_set': { rest: yes, mcp: mcp('billing.key_priority_set'), ui: yes },
  // #7810: hand-edit one model of an org key's catalogue (tier / active, or add it),
  // audited `billing.key.model_edited`; the catalogue sync keeps it current otherwise.
  'billing.key_model_set': {
    rest: yes,
    mcp: mcp('billing.key_model_set'),
    ui: gap('no dashboard model editor on the key card yet; #7810 ships REST + MCP'),
  },
  // Capacity-aware scheduling: declare an org key's concurrency (`byok_keys.capacity`),
  // audited `billing.key.capacity_changed`. UI: the AI key card's "Jobs at once"
  // editor (#6863), admins only.
  'billing.key_capacity_set': { rest: yes, mcp: mcp('billing.key_capacity_set'), ui: yes },
  // #5203: rename an org key's route-pin handle (`byok_keys.label`), carrying every
  // `llm_routes.key_label` pin that named it in one transaction, audited
  // `billing.key.label_changed` + one `llm_route.set` per pin. REST-only in this slice.
  'billing.key_label_set': {
    rest: yes,
    mcp: gap('no MCP `billing key_label_set` twin yet: declared gap of #5203, owed separately'),
    ui: gap('no dashboard control renames a key yet; #5203 ships the REST route only'),
  },
  // #5118: the account owner's view of an open key outage (`GET /v1/key-outage`,
  // `Scope.ReadBilling`). Surfaces #5119's banner and #5124's status block read
  // the same projection; MCP reads through #5124.
  'billing.outage_get': {
    rest: yes,
    mcp: na(
      'read through the status verb key_outage block; status is claimed once, by account.status',
    ),
    ui: gap('the account banner, #5119'),
  },
  // #5118: the account owner's pick (`POST /v1/key-outage/pick`, `Scope.WriteBilling`).
  // Closes the outage `picked` and pins every still-held task/ask to the named
  // key; the MCP `billing outage_pick` action lands in #5125.
  'billing.outage_pick': {
    rest: yes,
    mcp: mcp('billing.outage_pick'),
    ui: gap('the banner key picker, #5119'),
  },
  // The "N running / M capacity" view — also rides `status` and `box fleet_status`.
  // UI: each AI key card's live usage line and pushback warning (#6863).
  'billing.capacity': { rest: yes, mcp: mcp('billing.capacity'), ui: yes },
  'billing.usage': { rest: yes, mcp: mcp('billing.usage'), ui: yes },
  // `byok_keys.purpose` lane fence (#1490): the validated write path for
  // designating a key to a lane. MCP-only for now (the `billing key_purpose_*`
  // actions are the validated write seam; REST + UI are deliberate follow-ups).
  'billing.byok_key_purpose': {
    rest: gap('MCP-only validated write path for #1490; REST twin owed'),
    mcp: mcp('billing.key_purpose_set', 'billing.key_purpose_clear'),
    ui: gap('MCP-only validated write path for #1490; dashboard twin owed'),
  },
  'billing.entitlements': { rest: yes, mcp: mcp('billing.entitlements'), ui: yes },
  'billing.portal': { rest: yes, mcp: mcp('billing.portal'), ui: yes },
  'billing.checkout': { rest: yes, mcp: mcp('billing.checkout'), ui: yes },
  'billing.set_cap': { rest: yes, mcp: mcp('billing.set_cap'), ui: yes },
  'billing.log_sink_get': { rest: yes, mcp: mcp('billing.log_sink_get'), ui: yes },
  'billing.log_sink_set': { rest: yes, mcp: mcp('billing.log_sink_set'), ui: yes },
  'billing.log_sink_clear': { rest: yes, mcp: mcp('billing.log_sink_clear'), ui: yes },
  'billing.stripe_webhook': {
    rest: yes,
    mcp: na("Stripe-signed inbound leg — Stripe is the only caller and it doesn't speak JSON-RPC"),
    ui: na('an inbound webhook, not a control anyone operates'),
  },

  // ── LLM routing + system prompts ──────────────────────────────────────────
  // The MCP nouns are the two SCOPES a pin addresses (`org_*` / `repo_*`), which
  // is where they part ways from the REST ids: `project_*` names the resource the
  // route path carries, `repo_*` the `repositories` row the claim-time resolver
  // matches a pin by. Same divergence the `prompt` verb records one block down.
  'llm_route.list': { rest: yes, mcp: mcp('llm_route.list'), ui: yes },
  // The preview (#5207): what each purpose resolves to right now, from the same
  // control on both doors. The agents-page card that renders it is its own slice.
  'llm_route.preview': {
    rest: yes,
    mcp: mcp('llm_route.preview'),
    ui: gap('the agents-page "Right now" card that reads the preview is a later slice of #5200'),
  },
  // #7810: the org deny list (`account_model_policies`), models never run on any route.
  'llm_route.denied_models_get': {
    rest: yes,
    mcp: mcp('llm_route.denied_models'),
    ui: gap('no agents-page editor for the deny list yet; #7810 ships REST + MCP'),
  },
  'llm_route.denied_models_set': {
    rest: yes,
    mcp: mcp('llm_route.denied_models_set'),
    ui: gap('no agents-page editor for the deny list yet; #7810 ships REST + MCP'),
  },
  'llm_route.org_set': { rest: yes, mcp: mcp('llm_route.org_pin'), ui: yes },
  'llm_route.org_clear': { rest: yes, mcp: mcp('llm_route.org_clear'), ui: yes },
  'llm_route.project_set': { rest: yes, mcp: mcp('llm_route.repo_pin'), ui: yes },
  'llm_route.project_clear': { rest: yes, mcp: mcp('llm_route.repo_clear'), ui: yes },
  // Agent modes (plan 101 slice 06): the same org/repo split as the pins above —
  // REST `project_*` for the path's resource, MCP `repo_*` for the row it keys.
  // The UI is the account + repo settings' `AgentModesPanel`.
  'agent_mode.org_list': { rest: yes, mcp: mcp('agent_mode.org_list'), ui: yes },
  'agent_mode.org_set': { rest: yes, mcp: mcp('agent_mode.org_set'), ui: yes },
  'agent_mode.org_clear': { rest: yes, mcp: mcp('agent_mode.org_clear'), ui: yes },
  'agent_mode.project_list': { rest: yes, mcp: mcp('agent_mode.repo_list'), ui: yes },
  'agent_mode.project_set': { rest: yes, mcp: mcp('agent_mode.repo_set'), ui: yes },
  'agent_mode.project_clear': { rest: yes, mcp: mcp('agent_mode.repo_clear'), ui: yes },
  // Machine switches (#6756): each machine switches its own roles off.
  'agent_mode.machine_list': {
    rest: yes,
    mcp: mcp('agent_mode.machine_list'),
    ui: gap('the card capability switches ship in #6766'),
  },
  'agent_mode.machine_set': {
    rest: yes,
    mcp: mcp('agent_mode.machine_set'),
    ui: gap('the card capability switches ship in #6766'),
  },
  'agent_mode.machine_clear': {
    rest: yes,
    mcp: mcp('agent_mode.machine_clear'),
    ui: gap('the card capability switches ship in #6766'),
  },
  // The Agents page's one read (#5206): per roster agent, running now, last run, the
  // 7-day window and the org mode FLAG — never a mode's tool lists (epic #5200 D11).
  'agent.activity': {
    rest: yes,
    mcp: gap('no MCP read of per-agent activity yet; the REST read is the Agents page’s poll'),
    ui: gap('the Agents page that renders it ships in #5209; only its data hook exists'),
  },
  // Who last changed an agent's model or switch (#5208, epic #5200 G4): the newest
  // route/mode audit rows for that agent's purposes and modes, `read:billing`.
  'agent.history': {
    rest: yes,
    mcp: gap('no MCP read of per-agent change history yet; `audit tail` reads the raw rows'),
    ui: gap('the Agents page card that renders it ships with #5209'),
  },
  // Which pull requests and tasks one agent holds right now (#6210): the account's live
  // tasks of that agent's origins, or escalation's rescue rounds, `read:tasks`.
  'agent.queue': {
    rest: yes,
    mcp: gap("no MCP read of one agent's queue yet; `task list` reads the account queue"),
    ui: yes,
  },
  'system_prompt.org_get': { rest: yes, mcp: mcp('prompt.org_get'), ui: yes },
  'system_prompt.org_set': { rest: yes, mcp: mcp('prompt.org_set'), ui: yes },
  'system_prompt.org_clear': { rest: yes, mcp: mcp('prompt.org_clear'), ui: yes },
  // The MCP `repo_*` actions take the repository UUID, which is the ref the
  // assigner actually resolves a repo layer by; the REST path param admits a
  // looser charset for historical reasons. A narrower field on one surface is not
  // an unserved capability — the "one capability, not one field" rule, the
  // `project.update` / `org_sso.set` precedent.
  'system_prompt.project_get': { rest: yes, mcp: mcp('prompt.repo_get'), ui: yes },
  'system_prompt.project_set': { rest: yes, mcp: mcp('prompt.repo_set'), ui: yes },
  'system_prompt.project_clear': { rest: yes, mcp: mcp('prompt.repo_clear'), ui: yes },

  // ── integrations ──────────────────────────────────────────────────────────
  'integration.list': { rest: yes, mcp: mcp('integration.list'), ui: yes },
  'integration.get': {
    rest: gap('no point read — `GET /v1/integrations` is the only door'),
    mcp: mcp('integration.get'),
    ui: na(
      'the integrations page renders from the list, which carries every field the point read does',
    ),
  },
  'integration.connect': {
    rest: yes,
    mcp: na(
      'credentials never cross the MCP boundary — there is no connect verb, by design ' +
        '(packages/mcp/src/army/integration.ts)',
    ),
    ui: yes,
  },
  'integration.disconnect': { rest: yes, mcp: mcp('integration.disconnect'), ui: yes },

  // ── connected webhook sources (arc 101 doc 16) ─────────────────────────────
  //
  // `POST /hooks/:source_id` connections — one url and one secret per tracker or
  // CI system, whose ROW is the tenant. They ride the `integration` verb because
  // they answer the same question and are managed with the same scopes.
  //
  // REST is an owed GAP rather than an `na`: nothing about the capability is
  // agent-shaped, and a maintainer connecting their own Linear workspace from a
  // dashboard is the primary case. It is not served yet because the connect leg
  // needs a mounted route, a catalog entry and an OpenAPI operation — its own
  // change, not something to half-land beside the lane it feeds.
  'webhook_source.list': {
    rest: gap('the connect surface is owed a REST twin — see the connected-sources note'),
    mcp: mcp('integration.source_list'),
    ui: gap('no connections screen for inbound sources yet'),
  },
  'webhook_source.connect': {
    rest: gap('the connect surface is owed a REST twin — see the connected-sources note'),
    mcp: mcp('integration.source_connect'),
    ui: gap('no connections screen for inbound sources yet'),
  },
  'webhook_source.rotate': {
    rest: gap('the connect surface is owed a REST twin — see the connected-sources note'),
    mcp: mcp('integration.source_rotate'),
    ui: gap('no connections screen for inbound sources yet'),
  },
  'webhook_source.update': {
    rest: gap('the connect surface is owed a REST twin — see the connected-sources note'),
    mcp: mcp('integration.source_update'),
    ui: gap('no connections screen for inbound sources yet'),
  },
  'webhook_source.disconnect': {
    rest: gap('the connect surface is owed a REST twin — see the connected-sources note'),
    mcp: mcp('integration.source_disconnect'),
    ui: gap('no connections screen for inbound sources yet'),
  },

  // ── donations ─────────────────────────────────────────────────────────────
  'donation.list': { rest: yes, mcp: mcp('donation.list'), ui: yes },
  'donation.create': { rest: yes, mcp: mcp('donation.create'), ui: yes },
  'donation.cancel': { rest: yes, mcp: mcp('donation.cancel'), ui: yes },
  'donation.received': { rest: yes, mcp: mcp('donation.received'), ui: yes },
  'donation.decline': { rest: yes, mcp: mcp('donation.decline'), ui: yes },
  'donation.set_model': { rest: yes, mcp: mcp('donation.set_model'), ui: yes },

  // ── notifications + push + email routing ──────────────────────────────────
  'notification.inbox_list': { rest: yes, mcp: mcp('notification.inbox'), ui: yes },
  'notification.mark_read': { rest: yes, mcp: mcp('notification.read'), ui: yes },
  'notification.mark_all_read': { rest: yes, mcp: mcp('notification.read_all'), ui: yes },
  'notification.prefs_list': { rest: yes, mcp: mcp('notification.prefs_get'), ui: yes },
  // Notifications overhaul, slice 12.
  'notification.prefs_replace': {
    rest: yes,
    mcp: mcp('notification.prefs_replace'),
    ui: gap(
      'the Notifications page saves presets and timing through it in notifications-overhaul slice 19',
    ),
  },
  'notification.pref_set': { rest: yes, mcp: mcp('notification.prefs_set'), ui: yes },
  'notification.pref_clear': { rest: yes, mcp: mcp('notification.prefs_clear'), ui: yes },
  // Notifications overhaul, slice 14. The dashboard button is slice 20's.
  'notification.test_email': {
    rest: yes,
    mcp: mcp('notification.test_email'),
    ui: gap(
      'the Notifications page gains "Send me a test email" in notifications-overhaul slice 20',
    ),
  },
  // Notifications overhaul, slice 13 (#5699). The page copy is slice 21's.
  'notification.estimate': {
    rest: yes,
    mcp: mcp('notification.estimate'),
    ui: gap('the Notifications page shows the weekly estimate in notifications-overhaul slice 21'),
  },
  'notification.deliveries_list': {
    rest: yes,
    mcp: mcp('notification.deliveries'),
    ui: gap('the Notifications page lists recent email in notifications-overhaul slice 21'),
  },
  'email_status.get': {
    rest: yes,
    mcp: gap('the notification verb does not report hard-bounce suppression yet'),
    ui: yes,
  },
  'email_status.retry': {
    rest: yes,
    mcp: gap('the notification verb does not lift a hard-bounce suppression yet'),
    ui: yes,
  },
  'notification.unsubscribe_page': {
    rest: yes,
    mcp: na('a signed one-category link for a reader who may have no login at all'),
    ui: na('it renders its own page precisely because the reader may not be signed in'),
  },
  'notification.unsubscribe': {
    rest: yes,
    mcp: na('a signed one-category link (RFC 8058 one-click), not a credentialed call'),
    ui: na('served from the email footer, not from the dashboard'),
  },
  'push.public_key': { rest: yes, mcp: PUSH_IS_BROWSER, ui: yes },
  'push.subscriptions_list': { rest: yes, mcp: PUSH_IS_BROWSER, ui: yes },
  'push.subscribe': { rest: yes, mcp: PUSH_IS_BROWSER, ui: yes },
  'push.unsubscribe': { rest: yes, mcp: PUSH_IS_BROWSER, ui: yes },
  'email_route.list': { rest: yes, mcp: mcp('email_route.list'), ui: yes },
  'email_route.set': { rest: yes, mcp: mcp('email_route.set'), ui: yes },
  'email_route.delete': { rest: yes, mcp: mcp('email_route.delete'), ui: yes },

  // ── outbound webhooks ─────────────────────────────────────────────────────
  'webhook.list': { rest: yes, mcp: mcp('webhook.list'), ui: yes },
  'webhook.get': {
    rest: gap('no point read — `GET /v1/webhooks` is the only door'),
    mcp: mcp('webhook.get'),
    ui: na(
      'the webhooks page renders from the list, which carries every field the point read does',
    ),
  },
  'webhook.create': { rest: yes, mcp: mcp('webhook.create'), ui: yes },
  'webhook.delete': { rest: yes, mcp: mcp('webhook.delete'), ui: yes },
  'webhook.test': { rest: yes, mcp: mcp('webhook.test'), ui: yes },
  'webhook.deliveries': { rest: yes, mcp: mcp('webhook.deliveries'), ui: yes },
};
